import type { Account, PositionAssetClass } from '@fleece/models';
import type { Decimal } from '@fleece/utilities';
import { RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge, Button, StatusDot } from '@/components/ui';
import { useLedgerRefresh } from '@/hooks/use-ledger';
import { money, optionContract, tone } from '@/lib/ledger';
import { cn } from '@/lib/utils';

/** Live stands out, as a live key does on the credentials page: it is real money. */
export function AccountTypeBadge({ type }: { type: Account['accountType'] }) {
  switch (type) {
    case 'live':
      return <Badge tone="negative">Live</Badge>;
    case 'paper':
      return <Badge>Paper</Badge>;
    case 'mirror':
      return <Badge>Mirror</Badge>;
  }
}

export function AccountStatus({ status }: { status: Account['status'] }) {
  return status === 'active' ? <StatusDot tone="positive" label="Active" /> : <StatusDot tone="muted" label="Inactive" />;
}

const TONE_TEXT = { positive: 'text-positive', negative: 'text-negative', muted: 'text-ink-2' } as const;

/** A profit or a loss: coloured, and signed, so the colour is never the only thing that says which. */
export function Pnl({ value, className }: { value: Decimal | undefined; className?: string }) {
  return (
    <span className={cn('tabular', TONE_TEXT[tone(value)], className)} title={value?.toString()}>
      {money(value)}
    </span>
  );
}

/**
 * A symbol as a person reads it. An option's OCC symbol says its terms in a code, so the
 * terms are written out and the code is kept as the title for searching by.
 */
export function SymbolCell({ symbol, assetClass }: { symbol: string; assetClass: PositionAssetClass }) {
  if (assetClass === 'cash') {
    return (
      <span className="font-medium" title={symbol}>
        Cash
      </span>
    );
  }
  const contract = optionContract(symbol, assetClass);
  if (contract === undefined) {
    return <span className="font-medium">{symbol}</span>;
  }
  return (
    <span className="flex flex-col gap-0.5" title={symbol}>
      <span className="font-medium whitespace-nowrap">
        {contract.root} {contract.strike} {contract.right}
      </span>
      <span className="text-[11px] whitespace-nowrap text-muted">exp {contract.expiration}</span>
    </span>
  );
}

/** One headline figure above the tables. */
export function Stat({ label, children, detail }: { label: string; children: ReactNode; detail?: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-md border border-line bg-surface px-3 py-2.5">
      <span className="text-[10px] font-semibold tracking-wider text-muted uppercase">{label}</span>
      <span className="text-lg leading-tight font-semibold tabular">{children}</span>
      {detail !== undefined && <span className="truncate text-[11px] text-muted">{detail}</span>}
    </div>
  );
}

/** Reads the ledger's figures again; positions and profit move with every fill. */
export function RefreshButton() {
  const { refresh, refreshing } = useLedgerRefresh();
  return (
    <Button variant="ghost" onClick={refresh} disabled={refreshing} aria-label="Refresh" title="Refresh">
      <RefreshCw className={cn('size-3.5', refreshing && 'animate-spin')} />
    </Button>
  );
}
